import React, {useEffect, useState} from "react";
import {collection, getDocs, query, where} from "firebase/firestore";
import {httpsCallable} from "@firebase/functions";
import {Link, NavLink, useLocation} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft, faArrowsRotate, faBed, faCamera, faCheckCircle, faCoins, faHeart, faLocationDot, faMap, faMessage} from "@fortawesome/free-solid-svg-icons";
import {db, functions} from "../../firebase/FirebaseProvider";
import {BadgeStatus, Location, User, UserBadge, UserStats} from "../types/Types";
import {getCountryFlag} from "../util/Util";
import {getBadgeIcon} from "../util/BadgeUtil";
import {V2GlassChip, V2GlassPanel, V2GlassSurface} from "../shared/v2/V2Glass";
import Journey from "../map/Journey";
import "./UserDetailsV2.css";

function formatCheckInDuration(hours: number | undefined): string {
  const totalMinutes = Math.max(0, Math.floor((hours ?? 0) * 60));
  if (totalMinutes === 0) {
    return "0 minutes";
  }

  const units = [
    {label: "year", minutes: 365 * 24 * 60},
    {label: "month", minutes: 30 * 24 * 60},
    {label: "week", minutes: 7 * 24 * 60},
    {label: "day", minutes: 24 * 60},
    {label: "hour", minutes: 60},
    {label: "minute", minutes: 1},
  ];
  let remainingMinutes = totalMinutes;
  const parts: Array<string> = [];

  units.forEach((unit) => {
    if (parts.length >= 2) {
      return;
    }
    const value = Math.floor(remainingMinutes / unit.minutes);
    if (value > 0) {
      parts.push(`${value} ${unit.label}${value === 1 ? "" : "s"}`);
      remainingMinutes -= value * unit.minutes;
    }
  });

  return parts.join(" ");
}

function formatWidgetValue(value: number | string): number | string {
  return typeof value === "number" && Math.abs(value) > 999
    ? value.toLocaleString("en-US")
    : value;
}

interface Props {
  currentUser: User | undefined;
  user: User | undefined;
  locations: Array<Location> | undefined;
  detailHeader?: React.ReactNode;
  detailActions?: React.ReactNode;
  detailStatActions?: Record<string, {onClick: () => void; active: boolean}>;
  detailStatValues?: Record<string, number>;
}

const activeBadgesCache = new Map<string, Array<UserBadge>>();
const activeBadgesRequests = new Map<string, Promise<Array<UserBadge>>>();

function loadActiveBadges(userId: string): Promise<Array<UserBadge>> {
  const cachedBadges = activeBadgesCache.get(userId);
  if (cachedBadges) {
    return Promise.resolve(cachedBadges);
  }

  const existingRequest = activeBadgesRequests.get(userId);
  if (existingRequest) {
    return existingRequest;
  }

  const request = getDocs(
    query(
      collection(db, "user-badges"),
      where("userId", "==", userId),
      where("status", "==", BadgeStatus.ACTIVE)
    )
  ).then((badgesSnap) => {
    const badges: Array<UserBadge> = [];
    badgesSnap.forEach(doc => badges.push({...doc.data(), id: doc.id} as UserBadge));
    badges.sort((a, b) => a.order - b.order);
    activeBadgesCache.set(userId, badges);
    return badges;
  }).finally(() => {
    activeBadgesRequests.delete(userId);
  });

  activeBadgesRequests.set(userId, request);
  return request;
}

function UserDetailsV2(props: Props) {
  const [activeBadges, setActiveBadges] = useState<Array<UserBadge>>([]);
  const [calculatedStats, setCalculatedStats] = useState<UserStats | undefined>(undefined);
  const [refreshingStats, setRefreshingStats] = useState<boolean>(false);
  const [statsRefreshFailed, setStatsRefreshFailed] = useState<boolean>(false);
  const [refreshMessage, setRefreshMessage] = useState<string>("");
  const [showJourneyMap, setShowJourneyMap] = useState<boolean>(false);
  const [stayCountOverride, setStayCountOverride] = useState<number | undefined>(undefined);
  const currentLocation = useLocation();
  const viewedUserId = props.user?.id;
  const viewedUserStats = props.user?.stats;
  const hasCheckIns = !!props.user && props.user.sessionCount > 0;
  const canViewSocial = !!props.currentUser && !props.currentUser.socialDisabled;
  const isSuperAdmin = props.currentUser?.id === "XF6i86GWnUh6pFR8GvKr93bFsOu2";
  const countriesVisited = Array.from(new Set((props.user?.countriesVisited || []).filter(Boolean)));
  const hasCompleteStats = viewedUserStats?.statsVersion === 12
    && viewedUserStats?.comments !== undefined
    && viewedUserStats?.likes !== undefined
    && viewedUserStats?.photos !== undefined
    && viewedUserStats?.visits !== undefined
    && viewedUserStats?.stays !== undefined
    && viewedUserStats?.checkIns !== undefined
    && viewedUserStats?.uniqueCheckIns !== undefined
    && viewedUserStats?.checkInTime !== undefined
    && viewedUserStats?.points !== undefined
    && viewedUserStats?.pointsTotal !== undefined;
  const showRefreshingStats = !!props.currentUser
    && !!viewedUserId
    && !statsRefreshFailed
    && (refreshingStats || (!calculatedStats && !hasCompleteStats));
  const userStats = calculatedStats || viewedUserStats || {};
  const persistedStayCount = Math.max(Number(userStats.stays ?? 0), Number(props.user?.stayCount ?? 0));
  const stayCount = stayCountOverride ?? persistedStayCount;
  const hasStays = stayCount > 0;
  const profilePathParts = currentLocation.pathname.split("/").filter(Boolean);
  const profileTab = profilePathParts[0] === "profile" && profilePathParts[1] === viewedUserId
    ? profilePathParts[2]
    : undefined;
  const detailStatsByTab: Record<string, Array<{label: string; value: number}>> = {
    visits: [
      {label: "Visits", value: props.detailStatValues?.Visits ?? userStats.visits ?? props.user?.visitCount ?? 0},
      {label: "Verified", value: props.detailStatValues?.Verified ?? props.user?.sessionVerifiedCount ?? 0},
    ],
    sessions: [
      {label: "Check-ins", value: props.detailStatValues?.["Check-ins"] ?? userStats.checkIns ?? 0},
      {label: "Unique", value: props.detailStatValues?.Unique ?? userStats.uniqueCheckIns ?? 0},
    ],
    comments: [{label: "Comments", value: userStats.comments ?? 0}],
    photos: [{label: "Photos", value: userStats.photos ?? props.user?.photoCount ?? 0}],
    likes: [{label: "Likes", value: userStats.likes ?? 0}],
    points: [{label: "Points", value: props.detailStatValues?.Points ?? Math.floor(userStats.pointsTotal ?? userStats.points ?? props.user?.bonusPoints ?? 0)}],
    stays: [{label: "Stays", value: props.detailStatValues?.Stays ?? userStats.stays ?? props.user?.stayCount ?? 0}],
  };
  const detailStats = profileTab ? detailStatsByTab[profileTab] : undefined;
  const isProfileDetail = !!detailStats;
  const profileLinks = props.user ? [
    {
      ariaLabel: "Visits",
      label: "Visits",
      icon: faCheckCircle,
      to: `/profile/${props.user.id}/visits`,
      value: userStats.visits ?? props.user.visitCount ?? 0,
      visible: true,
    },
    {
      ariaLabel: "Check-ins",
      label: "Check-ins",
      icon: faLocationDot,
      to: `/profile/${props.user.id}/sessions`,
      value: userStats.checkIns ?? 0,
      visible: true,
    },
    {
      ariaLabel: "Comments",
      label: "Comments",
      icon: faMessage,
      to: `/profile/${props.user.id}/comments`,
      value: userStats.comments ?? 0,
      visible: canViewSocial,
    },
    {
      ariaLabel: "Photos",
      label: "Photos",
      icon: faCamera,
      to: `/profile/${props.user.id}/photos`,
      value: userStats.photos ?? props.user.photoCount ?? 0,
      visible: canViewSocial,
    },
    {
      ariaLabel: "Likes",
      label: "Likes",
      icon: faHeart,
      to: `/profile/${props.user.id}/likes`,
      value: userStats.likes ?? 0,
      visible: canViewSocial,
    },
    {
      ariaLabel: "Points",
      label: "Points",
      icon: faCoins,
      to: `/profile/${props.user.id}/points`,
      value: Math.floor(userStats.pointsTotal ?? userStats.points ?? props.user.bonusPoints ?? 0),
      visible: canViewSocial && hasCheckIns,
    },
    {
      ariaLabel: "Stays",
      label: "Stays",
      icon: faBed,
      to: `/profile/${props.user.id}/stays`,
      value: stayCount,
      visible: hasStays,
    },
  ] : [];
  const visibleProfileLinks = profileLinks.filter(link => link.visible && Number(link.value) > 0);

  const forceRefreshUserStats = async () => {
    if (!viewedUserId || refreshingStats) {
      return;
    }

    setRefreshingStats(true);
    setStatsRefreshFailed(false);
    setRefreshMessage("");
    try {
      const ensureUserStats = httpsCallable(functions, "ensureUserStats");
      const result = await ensureUserStats({userId: viewedUserId, force: true});
      const data = result.data as {stats?: UserStats};
      if (data.stats) {
        setCalculatedStats(data.stats);
      }
      setRefreshMessage("Stats refreshed");
    } catch (error) {
      console.error("Failed to refresh user stats", error);
      setStatsRefreshFailed(true);
      setRefreshMessage("Refresh failed");
    } finally {
      setRefreshingStats(false);
    }
  };

  const toggleJourneyMap = () => setShowJourneyMap((showMap) => !showMap);
  const handleJourneyMapKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleJourneyMap();
    }
  };

  useEffect(() => {
    setStayCountOverride(undefined);
  }, [viewedUserId]);

  useEffect(() => {
    if (!viewedUserId) {
      return;
    }

    const handleStayCountChanged = (event: Event) => {
      const detail = (event as CustomEvent<{adjustment?: number; userId?: string; userStayCount?: number}>).detail;
      if (detail?.userId !== viewedUserId) {
        return;
      }

      if (typeof detail.userStayCount === "number") {
        setStayCountOverride(detail.userStayCount);
      } else if (typeof detail.adjustment === "number") {
        setStayCountOverride((count) => Math.max(0, (count ?? persistedStayCount) + detail.adjustment!));
      }
    };

    window.addEventListener("stl:stay-count-changed", handleStayCountChanged);
    return () => window.removeEventListener("stl:stay-count-changed", handleStayCountChanged);
  }, [persistedStayCount, viewedUserId]);

  useEffect(() => {
    if (!viewedUserId || props.user?.badgeDisabled) {
      setActiveBadges([]);
      return;
    }

    let cancelled = false;
    loadActiveBadges(viewedUserId)
      .then((badges) => {
        if (!cancelled) {
          setActiveBadges(badges);
        }
      })
      .catch((error) => {
        console.error("Failed to load active user badges", error);
      });

    return () => {
      cancelled = true;
    };
  }, [viewedUserId, props.user?.badgeDisabled]);

  useEffect(() => {
    setCalculatedStats(undefined);
    setStatsRefreshFailed(false);
    if (!props.currentUser
      || !viewedUserId
      || hasCompleteStats) {
      return;
    }

    (async () => {
      try {
        setRefreshingStats(true);
        const ensureUserStats = httpsCallable(functions, "ensureUserStats");
        const result = await ensureUserStats({userId: viewedUserId});
        const data = result.data as {stats?: UserStats};
        if (data.stats) {
          setCalculatedStats(data.stats);
        }
      } catch (error) {
        console.error("Failed to calculate user stats", error);
        setStatsRefreshFailed(true);
      } finally {
        setRefreshingStats(false);
      }
    })();
  }, [props.currentUser, viewedUserId, hasCompleteStats]);

  useEffect(() => {
    setShowJourneyMap(false);
  }, [viewedUserId]);

  return (
    <>
      <V2GlassPanel className="user-v2-header">
        {!props.user && <div className="user-v2-loading">...</div>}
        {props.user && (
          <>
            <V2GlassSurface className="user-v2-title-card">
              <div className="user-v2-title-row">
                <h1>{props.user.name}</h1>
                {isSuperAdmin && (
                  <div className="user-v2-admin-controls">
                    <button
                      className="user-v2-refresh-button"
                      type="button"
                      onClick={forceRefreshUserStats}
                      disabled={refreshingStats}
                      aria-label="Refresh stats"
                      title="Refresh stats"
                    >
                      <FontAwesomeIcon icon={faArrowsRotate} spin={refreshingStats} />
                    </button>
                    {refreshMessage && <span className="user-v2-refresh-message">{refreshMessage}</span>}
                  </div>
                )}
              </div>
              {activeBadges.length > 0 && (
                <div className="user-v2-badges">
                  {activeBadges.map(badge => (
                    <V2GlassChip className="user-v2-badge-chip" key={badge.id ?? badge.badgeType}>
                      <FontAwesomeIcon icon={getBadgeIcon(badge.badgeType)} />
                      {badge.description}
                    </V2GlassChip>
                  ))}
                </div>
              )}
            </V2GlassSurface>
          </>
        )}
      </V2GlassPanel>

      {props.user && isProfileDetail && detailStats && (
        <V2GlassPanel className="user-v2-detail-toolbar">
          <div className="user-v2-detail-toolbar-main">
            <Link className="user-v2-back-button" to={`/profile/${props.user.id}`}>
              <FontAwesomeIcon icon={faArrowLeft} />
              Profile
            </Link>
            <div className="user-v2-detail-stats">
              {detailStats.map((stat) => (
                props.detailStatActions?.[stat.label] ? (
                  <button
                    className={`user-v2-detail-stat user-v2-detail-stat-button${props.detailStatActions[stat.label].active ? " user-v2-detail-stat-button-active" : ""}`}
                    disabled={props.detailStatActions[stat.label].active}
                    key={stat.label}
                    onClick={props.detailStatActions[stat.label].onClick}
                    type="button"
                  >
                    <span>{stat.label}</span>
                    <b>{stat.value}</b>
                  </button>
                ) : (
                  <V2GlassSurface className="user-v2-detail-stat" key={stat.label}>
                    <span>{stat.label}</span>
                    <b>{stat.value}</b>
                  </V2GlassSurface>
                )
              ))}
              {props.detailActions}
            </div>
          </div>
          {props.detailHeader}
        </V2GlassPanel>
      )}

      {props.user && !isProfileDetail && (showRefreshingStats || visibleProfileLinks.length > 0) && (
        <V2GlassPanel className="user-v2-widgets-panel">
          {showRefreshingStats ? (
            <div className="user-v2-stats-refreshing">Refreshing stats...</div>
          ) : (
            <div className="user-v2-widget-grid">
              {visibleProfileLinks.map(link => {
                const widget = (
                  <V2GlassSurface className="user-v2-widget">
                    {link.value !== undefined && <b className={typeof link.value === "string" ? "user-v2-widget-value-text" : undefined}>{formatWidgetValue(link.value)}</b>}
                    <span className="user-v2-widget-label">
                      <FontAwesomeIcon icon={link.icon} />
                      {link.label}
                    </span>
                  </V2GlassSurface>
                );

                return link.to ? (
                  <NavLink
                    className={({isActive}) => `user-v2-widget-link${isActive ? " active" : ""}`}
                    to={link.to}
                    aria-label={link.ariaLabel}
                    title={link.label}
                    key={link.label}
                  >
                    {widget}
                  </NavLink>
                ) : (
                  <div className="user-v2-widget-link" aria-label={link.ariaLabel} title={link.label} key={link.label}>
                    {widget}
                  </div>
                );
              })}
            </div>
          )}
        </V2GlassPanel>
      )}

      {props.user && !isProfileDetail && canViewSocial && hasCheckIns && (
        <V2GlassPanel className="user-v2-user-stats-panel">
          <h2>User Stats</h2>
          {showRefreshingStats ? (
            <div className="user-v2-stats-refreshing">Refreshing stats...</div>
          ) : (
            <V2GlassSurface className="user-v2-user-stats-table">
              <div className="user-v2-user-stat-row">
                <span>Countries Visited</span>
                <b className="user-v2-country-flags">
                  {countriesVisited.map((country) => (
                    <span key={country}>{getCountryFlag(country)}</span>
                  ))}
                </b>
              </div>
              <div className="user-v2-user-stat-row">
                <span>Unique Check-ins</span>
                <b>{userStats.uniqueCheckIns ?? 0}</b>
              </div>
              <div className="user-v2-user-stat-row">
                <span>Time Checked In</span>
                <b>{formatCheckInDuration(userStats.checkInTime)}</b>
              </div>
            </V2GlassSurface>
          )}
        </V2GlassPanel>
      )}

      {props.user && !isProfileDetail && canViewSocial && hasCheckIns && (
        <V2GlassPanel className="user-v2-journey-panel">
          <V2GlassSurface
            className="user-v2-journey-card"
            onClick={toggleJourneyMap}
            onKeyDown={handleJourneyMapKeyDown}
            role="button"
            tabIndex={0}
            aria-expanded={showJourneyMap}
            aria-label="Toggle journey map"
          >
              <div className="user-v2-journey-content">
                <FontAwesomeIcon className="user-v2-journey-icon" icon={faMap} />
                <div>
                  <b>Journey</b>
                  <span>{userStats.uniqueCheckIns ?? 0} unique check-ins</span>
                </div>
              </div>
              <span className="user-v2-journey-action">{showJourneyMap ? "Hide map" : "View map"}</span>
          </V2GlassSurface>
          {showJourneyMap && props.locations && (
            <Journey
              locations={props.locations}
              currentUser={props.currentUser}
              profileUser={props.user}
              inline={true}
            />
          )}
        </V2GlassPanel>
      )}

    </>
  );
}

export default UserDetailsV2;
